import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <zerbay-reviews> (hz/55) — the "From You" block on the product page.
 *
 * Markup comes from sections/zerbay-product-reviews.liquid. This module
 *   1. pulls every review of the product from Judge.me's public widget endpoint (page after page, as soon as it runs),
 *   2. shows them as photo-first cards in two columns, with rating filters and a "see more" step,
 *   3. sends new reviews to Judge.me from the inline form,
 *   4. opens a review in a native <dialog> (see ReviewViewer below),
 *   5. wires the rating badge under the product title to the block (contract C-4).
 *
 * data-state on the element: loading -> ready | empty | summary
 *   empty   nothing to show and nothing promised by the server
 *   summary the server printed a count but the endpoint returned nothing: keep the printed numbers, hide the filters
 *
 * @typedef {object} Refs
 * @property {HTMLElement} score
 * @property {HTMLElement} scoreStars
 * @property {HTMLElement} countNum
 * @property {HTMLElement} countWord
 * @property {HTMLElement} bars
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} formPanel
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} send
 * @property {HTMLElement} notice
 * @property {HTMLElement} grid
 * @property {HTMLElement} moreRow
 * @property {HTMLButtonElement} moreBtn
 * @property {HTMLDialogElement} viewer
 *
 * @extends Component<Refs>
 */

const BATCH = 20;
const MAX_BATCHES = 20;
const SWIPE_DISTANCE = 50;
const ENDPOINT_LIST = 'https://judge.me/api/v1/widgets/product_review';
const ENDPOINT_POST = 'https://judge.me/api/v1/reviews';
const WIDE_QUERY = '(min-width: 750px)';

const isWide = () => window.matchMedia(WIDE_QUERY).matches;
const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Small DOM builder: h('p', 'class', 'text'). */
function h(tag, className = '', text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Markup for five stars; a half star starts at .5 (same rule as the server-printed average). */
function starMarkup(value, spoken = false) {
  const v = Number(value) || 0;
  const whole = Math.floor(v);
  let out = '';
  for (let n = 1; n <= 5; n++) {
    const kind = n <= whole ? 'on' : n === whole + 1 && v - whole >= 0.5 ? 'half' : 'off';
    out += `<span class="zb-reviews__star is-${kind}" aria-hidden="true"></span>`;
  }
  return spoken ? `${out}<span class="zb-reviews__sr">${Math.round(v * 10) / 10}/5</span>` : out;
}

/* ------------------------------------------------------------------ reading Judge.me's widget markup */

/** Photo address -> the sharper imgix rendition when it is a Judge.me image. */
function photoAddress(raw) {
  let address = String(raw ?? '').trim();
  const wrapped = address.match(/url\(\s*["']?([^"')]+)["']?\s*\)/);
  if (wrapped) address = wrapped[1];
  if (!address) return null;
  if (address.startsWith('//')) address = `${location.protocol}${address}`;
  try {
    const url = new URL(address, location.origin);
    if (url.hostname.includes('judgeme.imgix.net')) {
      url.searchParams.set('w', '600');
      url.searchParams.set('dpr', '2');
      return url.toString();
    }
  } catch (_error) {
    /* keep the address as it is */
  }
  return address;
}

function photosOf(node) {
  const found = new Set();
  const attrs = ['data-fullsize-url', 'data-original', 'src', 'data-src'];
  for (const img of node.querySelectorAll('.jdgm-rev__pics img, .jdgm-rev__pic img')) {
    let raw = attrs.map((a) => img.getAttribute(a)).find(Boolean);
    if (!raw) raw = (img.getAttribute('srcset') || img.getAttribute('data-srcset') || '').split(',')[0].trim().split(' ')[0];
    found.add(photoAddress(raw));
  }
  for (const link of node.querySelectorAll('a.jdgm-rev__pic')) {
    found.add(photoAddress(link.getAttribute('data-fullsize-url') || link.getAttribute('href')));
    found.add(photoAddress(link.getAttribute('style')));
  }
  found.delete(null);
  return [...found];
}

function ratingOf(node) {
  const holder = node.querySelector('[data-rating], .jdgm-rev__rating, .jdgm-star-rating');
  const direct = holder?.getAttribute('data-score') || holder?.getAttribute('data-rating');
  if (direct) return parseFloat(direct);
  const full = node.querySelectorAll('.jdgm-star.jdgm--on').length;
  return full + (node.querySelector('.jdgm-star.jdgm--half') ? 0.5 : 0);
}

function dateOf(node) {
  const stamp = node.querySelector('.jdgm-rev__timestamp');
  const text = (stamp?.getAttribute('data-content') || stamp?.textContent || '').trim();
  const iso = node.querySelector('time')?.getAttribute('datetime') || text;
  const parsed = new Date(iso.replace(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})\s*UTC$/i, '$1T$2Z'));
  return { text, iso: isNaN(parsed.getTime()) ? null : parsed.toISOString() };
}

function reviewFrom(node) {
  const when = dateOf(node);
  const text = (selector) => (node.querySelector(selector)?.textContent || '').trim();
  return {
    stars: ratingOf(node),
    heading: text('.jdgm-rev__title'),
    body: text('.jdgm-rev__body'),
    author: text('.jdgm-rev__author'),
    iso: when.iso,
    rawDate: when.text,
    photos: photosOf(node),
    verified: Boolean(node.querySelector('.jdgm-rev__buyer-badge')),
    votes: parseInt(node.querySelector('.jdgm-rev__votes-count')?.textContent || '0', 10) || 0,
  };
}

const identity = (r) => [r.heading.slice(0, 40), r.author.slice(0, 30), r.iso || '', r.body.slice(0, 40)].join('\u0001');

/** "17 Jun 2025" (Latin digits always): en pages follow the visitor's browser locale, other languages follow the page language. */
const dateFormats = new Map();
function showDate(review) {
  if (!review.iso) return review.rawDate || '';
  const lang = document.documentElement.lang || '';
  const locale = !lang || /^en/i.test(lang) ? undefined : lang;
  const id = locale || '';
  if (!dateFormats.has(id)) dateFormats.set(id, new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric', numberingSystem: 'latn' }));
  return dateFormats.get(id).format(new Date(review.iso));
}

/** Pages from the endpoint, one after another, until a short or empty page. */
async function* fetchPages(settings, signal) {
  for (let page = 1; page <= MAX_BATCHES; page++) {
    const url = new URL(ENDPOINT_LIST);
    url.search = new URLSearchParams({
      shop_domain: settings.shop,
      api_token: settings.key,
      external_id: settings.productId,
      page: String(page),
      per_page: String(BATCH),
    }).toString();
    let reviews = [];
    try {
      const response = await fetch(url, { signal });
      if (response.ok) {
        const { widget = '' } = await response.json();
        const doc = new DOMParser().parseFromString(widget, 'text/html');
        reviews = [...doc.querySelectorAll('.jdgm-rev')].flatMap((node) => {
          try {
            return [reviewFrom(node)];
          } catch (_error) {
            return [];
          }
        });
      }
    } catch (_error) {
      reviews = [];
    }
    if (!reviews.length) return;
    yield reviews;
    if (reviews.length < BATCH) return;
  }
}

/* ------------------------------------------------------------------ the dialog */

/** Detail view of one review on the block's native <dialog>: photos, neighbours, keys, swipe. */
class ReviewViewer {
  #r;
  #t;
  #list = [];
  #at = 0;
  #photos = [];
  #timer = 0;
  #holdsLock = false;
  #from = { x: 0, y: 0 };

  /** @param {Record<string, any>} refs @param {Record<string, string>} strings */
  constructor(refs, strings, signal) {
    this.#r = refs;
    this.#t = strings;
    const dialog = refs.viewer;
    const on = (target, type, fn, opts = {}) => target?.addEventListener(type, fn, { signal, ...opts });

    on(dialog, 'click', (e) => e.target === dialog && dialog.close());
    on(dialog, 'close', () => this.#released());
    on(dialog, 'keydown', (e) => this.#key(e));
    on(dialog, 'touchstart', (e) => this.#touchStart(e), { passive: true });
    on(dialog, 'touchend', (e) => this.#touchEnd(e), { passive: true });
    on(refs.closeX, 'click', () => dialog.close());
    on(refs.closeBack, 'click', () => dialog.close());
    on(refs.before, 'click', () => this.#move(-1));
    on(refs.after, 'click', () => this.#move(1));
    on(refs.thumbs, 'click', (e) => {
      const thumb = e.target instanceof Element ? e.target.closest('[data-photo]') : null;
      if (thumb) this.#pick(Number(thumb.getAttribute('data-photo')));
    });
  }

  get #rtl() {
    return getComputedStyle(this.#r.viewer).direction === 'rtl';
  }

  open(list, index) {
    if (!list[index]) return;
    this.#list = list;
    this.#show(index);
    const dialog = this.#r.viewer;
    if (dialog.open) return;
    lockScroll(dialog);
    document.documentElement.classList.add('zb-scroll-lock');
    this.#holdsLock = true;
    dialog.showModal();
  }

  release() {
    clearTimeout(this.#timer);
    this.#released();
  }

  #released() {
    clearTimeout(this.#timer);
    this.#r.viewer.classList.remove('is-fading');
    unlockScroll(this.#r.viewer);
    if (this.#holdsLock) document.documentElement.classList.remove('zb-scroll-lock');
    this.#holdsLock = false;
  }

  #show(index) {
    const r = this.#r;
    const review = this.#list[index];
    this.#at = index;

    r.vAuthor.textContent = review.author || this.#t.anonymous;
    r.vDate.textContent = showDate(review);
    r.vText.textContent = review.body;
    r.vStars.innerHTML = `<div class="zb-reviews__stars">${starMarkup(review.stars, true)}</div>`;

    const hasNeighbours = this.#list.length > 1 && isWide();
    r.before.hidden = r.after.hidden = !hasNeighbours;
    // aria-disabled, not disabled: a focused button that became disabled would drop focus and the arrow keys would stop
    r.before.setAttribute('aria-disabled', String(index === 0));
    r.after.setAttribute('aria-disabled', String(index === this.#list.length - 1));

    this.#photos = review.photos;
    r.thumbs.replaceChildren();
    r.gallery.hidden = this.#photos.length === 0;
    r.thumbs.hidden = this.#photos.length < 2;
    if (this.#photos.length) {
      r.photo.src = this.#photos[0];
      if (this.#photos.length > 1) {
        r.thumbs.append(
          ...this.#photos.map((src, n) => {
            const button = h('button', n === 0 ? 'zb-reviews__thumb is-current' : 'zb-reviews__thumb');
            button.type = 'button';
            button.dataset.photo = String(n);
            const img = document.createElement('img');
            img.src = src;
            img.alt = `${this.#t.photoAlt} ${n + 1}`;
            button.append(img);
            return button;
          })
        );
      }
    } else {
      r.photo.removeAttribute('src');
    }
    r.detail.scrollTop = 0;
  }

  #pick(n) {
    if (!this.#photos[n]) return;
    this.#r.photo.src = this.#photos[n];
    [...this.#r.thumbs.children].forEach((thumb, i) => thumb.classList.toggle('is-current', i === n));
  }

  #move(step) {
    const target = this.#at + step;
    if (target < 0 || target >= this.#list.length) return;
    const dialog = this.#r.viewer;
    clearTimeout(this.#timer);
    if (calm()) {
      this.#show(target);
      return;
    }
    dialog.classList.add('is-fading');
    this.#timer = window.setTimeout(() => {
      this.#show(target);
      requestAnimationFrame(() => dialog.classList.remove('is-fading'));
    }, 150);
  }

  #key(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const towardsEnd = (event.key === 'ArrowRight') !== this.#rtl;
    this.#move(towardsEnd ? 1 : -1);
  }

  #touchStart(event) {
    if (isWide()) return;
    const { screenX, screenY } = event.changedTouches[0];
    this.#from = { x: screenX, y: screenY };
  }

  #touchEnd(event) {
    if (isWide() || this.#list.length < 2) return;
    const { screenX, screenY } = event.changedTouches[0];
    const dx = this.#from.x - screenX;
    const dy = this.#from.y - screenY;
    if (Math.abs(dx) <= SWIPE_DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;
    // dragging towards the start edge of the reading direction brings the next review
    this.#move((dx > 0) !== this.#rtl ? 1 : -1);
  }
}

/* ------------------------------------------------------------------ the block */

class ZerbayReviews extends Component {
  requiredRefs = ['grid', 'viewer'];

  static get observedAttributes() {
    return ['data-product-id'];
  }

  #all = [];
  #sorted = [];
  #shownCount = BATCH;
  #only = null;
  #run = 0;
  #abort = null;
  #life = null;
  #viewer = null;
  #strings = {};
  #hideForm = 0;
  #formEpoch = 0;
  #postAbort = null;

  connectedCallback() {
    super.connectedCallback();
    const r = this.refs;
    // Texts arrive as data-t-* attributes: the HTML parser decodes the entities Shopify's `t` filter adds (apostrophes).
    const d = this.dataset;
    this.#strings = {
      anonymous: d.tAnonymous, verified: d.tVerified, verifiedBuyer: d.tVerifiedBuyer, photoAlt: d.tPhotoAlt,
      one: d.tOne, many: d.tMany, send: d.tSend, sending: d.tSending, sent: d.tSent, failed: d.tFailed,
    };

    this.#life = new AbortController();
    const signal = this.#life.signal;
    this.#viewer = new ReviewViewer(r, this.#strings, signal);

    r.bars.addEventListener('click', (e) => this.#onBar(e), { signal });
    r.moreBtn.addEventListener('click', () => this.#showMore(), { signal });
    r.toggle.addEventListener('click', () => this.#toggleForm(), { signal });
    r.form.addEventListener('submit', (e) => this.#submit(e), { signal });
    r.grid.addEventListener('click', (e) => this.#openFrom(e), { signal });
    r.grid.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target instanceof Element && e.target.matches('.zb-reviews__card')) {
        e.preventDefault();
        this.#openFrom(e);
      }
    }, { signal });

    // a reconnect can follow a dropped submission: the button must not stay in its "sending" state
    if (r.send) {
      r.send.disabled = false;
      r.send.textContent = this.#strings.send;
    }

    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#life?.abort();
    this.#abort?.abort();
    this.#dropPendingPost();
    this.#viewer?.release();
  }

  /** A submission belongs to one product and one connected lifetime: this ends any pending one without touching the page. */
  #dropPendingPost() {
    this.#formEpoch++;
    this.#postAbort?.abort();
    this.#postAbort = null;
    clearTimeout(this.#hideForm);
  }

  /** The element can survive a page morph to another product: start over for that product. */
  attributeChangedCallback(_name, before, after) {
    if (!this.isConnected || before === null || before === after) return;
    this.#all = [];
    this.#sorted = [];
    this.#only = null;
    this.#shownCount = BATCH;
    this.#dropPendingPost();
    const r = this.refs;
    if (r.send) {
      r.send.disabled = false;
      r.send.textContent = this.#strings.send;
    }
    if (r.notice) {
      r.notice.textContent = '';
      r.notice.className = 'zb-reviews__notice';
    }
    this.#load();
  }

  get #settings() {
    const d = this.dataset;
    return {
      key: (d.key || '').trim(),
      shop: d.shop || '',
      productId: d.productId || '',
      url: d.productUrl || '',
      order: d.sort || 'newest',
    };
  }

  /* ---- loading */

  async #load() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const ticket = ++this.#run;
    const settings = this.#settings;
    if (!settings.key || !settings.shop || !settings.productId) {
      this.dataset.state = 'empty';
      return;
    }
    this.dataset.state = 'loading';

    const seen = new Set();
    const collected = [];
    for await (const batch of fetchPages(settings, this.#abort.signal)) {
      if (ticket !== this.#run) return;
      for (const review of batch) {
        const id = identity(review);
        if (!seen.has(id)) {
          seen.add(id);
          collected.push(review);
        }
      }
    }
    if (ticket !== this.#run) return;

    this.#all = collected;
    this.#only = null;
    this.#shownCount = BATCH;
    if (collected.length) this.#paintAll();
    this.dataset.state = collected.length ? 'ready' : (parseInt(this.dataset.serverCount || '', 10) || 0) ? 'summary' : 'empty';
  }

  /* ---- painting */

  #paintAll() {
    this.#paintSummary();
    this.#sort();
    this.#paintBars();
    this.#paintGrid(0);
  }

  #tally() {
    const tally = [0, 0, 0, 0, 0, 0];
    for (const r of this.#all) {
      const bucket = Math.round(r.stars || 0);
      if (bucket >= 1 && bucket <= 5) tally[bucket]++;
    }
    return tally;
  }

  #paintSummary() {
    const r = this.refs;
    const n = this.#all.length;
    const mean = n ? this.#all.reduce((sum, x) => sum + (x.stars || 0), 0) / n : 0;
    r.countNum.textContent = String(n);
    r.countWord.textContent = n === 1 ? this.#strings.one : this.#strings.many;
    r.score.textContent = n ? mean.toFixed(2) : '—';
    r.scoreStars.innerHTML = n ? starMarkup(mean) : '';
  }

  #paintBars() {
    const tally = this.#tally();
    const n = this.#all.length || 1;
    this.refs.bars.replaceChildren(
      ...[5, 4, 3, 2, 1].map((level) => {
        const row = h('button', this.#only === level ? 'zb-reviews__bar is-picked' : 'zb-reviews__bar');
        row.type = 'button';
        row.dataset.level = String(level);
        row.setAttribute('aria-pressed', String(this.#only === level));
        const track = h('span', 'zb-reviews__bar-track');
        const fill = h('span', 'zb-reviews__bar-fill');
        fill.style.inlineSize = `${Math.round((tally[level] / n) * 100)}%`;
        track.append(fill);
        row.append(h('span', 'zb-reviews__bar-level', String(level)), track, h('span', 'zb-reviews__bar-total', `(${tally[level]})`));
        return row;
      })
    );
  }

  /** Photo reviews first, then the shop's chosen order. */
  #sort() {
    const when = (r) => (r.iso ? Date.parse(r.iso) : 0);
    const order = {
      newest: (a, b) => when(b) - when(a),
      oldest: (a, b) => when(a) - when(b),
      highest: (a, b) => b.stars - a.stars,
      lowest: (a, b) => a.stars - b.stars,
      helpful: (a, b) => b.votes - a.votes,
    }[this.#settings.order] || (() => 0);
    this.#sorted = this.#all
      .filter((r) => !this.#only || Math.round(r.stars || 0) === this.#only)
      .sort((a, b) => Number(b.photos.length > 0) - Number(a.photos.length > 0) || order(a, b));
  }

  #card(review) {
    const s = this.#strings;
    const card = h('article', 'zb-reviews__card');
    card.tabIndex = 0;
    /** @type {any} */ (card).review = review;

    if (review.photos.length) {
      const frame = h('div', 'zb-reviews__cover zb-shimmer');
      const img = document.createElement('img');
      Object.assign(img, { loading: 'lazy', decoding: 'async', src: review.photos[0], alt: s.photoAlt || '' });
      frame.append(img);
      card.append(frame);
    }

    const meta = h('div', 'zb-reviews__meta');
    meta.append(h('p', 'zb-reviews__author', review.author || s.anonymous));
    if (review.verified) {
      const badge = h('span', 'zb-reviews__verified', `✔︎ ${s.verified}`);
      badge.title = s.verifiedBuyer;
      meta.append(badge);
    }
    const time = h('time', 'zb-reviews__date', showDate(review));
    if (review.iso) time.dateTime = review.iso;
    meta.append(time);

    const stars = h('div', 'zb-reviews__stars');
    stars.innerHTML = starMarkup(review.stars, true);
    const text = h('span', 'zb-reviews__excerpt', review.body);

    const content = h('div', 'zb-reviews__content');
    content.append(meta, stars, text);
    card.append(content);
    return card;
  }

  /** Paints cards [from, shownCount) into whichever column is shorter. from = 0 rebuilds the grid. */
  #paintGrid(from) {
    const grid = this.refs.grid;
    if (from === 0) grid.replaceChildren(h('div', 'zb-reviews__col'), h('div', 'zb-reviews__col'));
    const columns = [...grid.children];
    for (const review of this.#sorted.slice(from, this.#shownCount)) {
      const shorter = columns[0].offsetHeight <= columns[1].offsetHeight ? columns[0] : columns[1];
      shorter.append(this.#card(review));
    }
    window.Zerbay?.shimmer?.scan?.(grid);
    this.refs.moreRow.hidden = this.#sorted.length <= this.#shownCount;
  }

  #onBar(event) {
    const row = event.target instanceof Element ? event.target.closest('[data-level]') : null;
    if (!row) return;
    const level = Number(row.getAttribute('data-level'));
    this.#only = this.#only === level ? null : level;
    this.#shownCount = BATCH;
    this.#sort();
    this.#paintGrid(0);
    this.#paintBars();
    this.refs.bars.querySelector(`[data-level="${level}"]`)?.focus({ preventScroll: true });
  }

  /** Adds cards below the existing ones, so nothing above them moves. */
  #showMore() {
    const from = this.#shownCount;
    this.#shownCount += BATCH;
    this.#paintGrid(from);
  }

  #openFrom(event) {
    if (!(event.target instanceof Element) || event.target.closest('a')) return;
    const card = event.target.closest('.zb-reviews__card');
    if (!card) return;
    const cards = [...this.refs.grid.querySelectorAll('.zb-reviews__card')];
    this.#viewer.open(cards.map((c) => /** @type {any} */ (c).review), cards.indexOf(card));
  }

  /* ---- writing a review */

  #toggleForm() {
    const r = this.refs;
    const opening = r.formPanel.hidden;
    r.formPanel.hidden = !opening;
    r.toggle.setAttribute('aria-expanded', String(opening));
    if (!opening) return;
    r.form.reset();
    r.form.classList.remove('is-checked');
    r.notice.textContent = '';
    r.notice.className = 'zb-reviews__notice';
    setTimeout(() => r.formPanel.scrollIntoView({ behavior: calm() ? 'auto' : 'smooth', block: 'nearest' }), 100);
  }

  async #submit(event) {
    event.preventDefault();
    const r = this.refs;
    r.form.classList.add('is-checked');
    if (!r.form.reportValidity()) return;

    const epoch = this.#formEpoch;
    const live = () => epoch === this.#formEpoch;
    const { shop, productId, url } = this.#settings;
    const abort = (this.#postAbort = new AbortController());
    r.send.disabled = true;
    r.send.textContent = this.#strings.sending;
    r.notice.textContent = '';
    r.notice.className = 'zb-reviews__notice';
    try {
      const body = new FormData(r.form);
      Object.entries({ platform: 'shopify', shop_domain: shop, id: productId, url }).forEach(([k, v]) => body.append(k, v));
      const response = await fetch(ENDPOINT_POST, { method: 'POST', body, signal: abort.signal });
      if (!live()) return;
      if (!response.ok) throw new Error('rejected');
      r.notice.textContent = this.#strings.sent;
      r.notice.className = 'zb-reviews__notice is-good';
      r.form.reset();
      r.form.classList.remove('is-checked');
      this.#hideForm = window.setTimeout(() => {
        if (!live()) return;
        r.formPanel.hidden = true;
        r.toggle.setAttribute('aria-expanded', 'false');
      }, 2000);
    } catch (_error) {
      if (!live()) return;
      r.notice.textContent = this.#strings.failed;
      r.notice.className = 'zb-reviews__notice is-bad';
    } finally {
      if (live()) {
        r.send.disabled = false;
        r.send.textContent = this.#strings.send;
        this.#postAbort = null;
      }
    }
  }
}

if (!customElements.get('zerbay-reviews')) customElements.define('zerbay-reviews', ZerbayReviews);

/* Contract C-4: the rating badge under the product title brings the visitor to this block.
   One listener per page by design: the module runs once and the handler looks the block up on every click. */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('[data-zb-review-badge]')) return;
  const block = document.getElementById('aggregated-reviews');
  if (!block) return;
  event.preventDefault();
  block.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
